import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Check, Mail } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refCode = sp.get("ref");
  const { branding } = useTheme();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null); // {email, message}

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await signup(form, { allowWaitlist: true });
      if (refCode) {
        try { await api.post("/referrals/redeem", { code: refCode }); } catch {/* */}
      }
      if (result?.waitlisted) {
        setWaitlisted({ email: form.email, message: result.message });
        return;
      }
      toast.success(`Welcome, ${result.user?.name || form.name}`);
      nav("/onboarding");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Signup failed"));
    } finally {
      setBusy(false);
    }
  };

  if (waitlisted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
        <div className="max-w-lg w-full border border-border p-10 bg-background" data-testid="signup-waitlist-screen">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Check className="w-6 h-6 text-primary" />
          </div>
          <div className="label-eyebrow mb-2">Thanks for signing up</div>
          <h1 className="heading-display text-3xl mb-4" data-testid="signup-waitlist-headline">You&apos;re on the {branding.platform_name} waitlist.</h1>
          <p className="text-sm text-foreground/80 mb-6 leading-relaxed">{waitlisted.message}</p>
          <div className="border border-border p-4 text-sm flex items-start gap-3" data-testid="signup-waitlist-email">
            <Mail className="w-4 h-4 mt-0.5 text-foreground/60" />
            <div>
              <div className="text-foreground/60 text-xs">We&apos;ll email <strong>{waitlisted.email}</strong></div>
              <div>as soon as your account is approved. You&apos;ll be able to log in immediately.</div>
            </div>
          </div>
          <div className="flex gap-3 mt-8">
            <Link to="/"><Button variant="outline" className="rounded-none" data-testid="signup-waitlist-home">Back to home</Button></Link>
            <Link to="/login"><Button className="rounded-none" data-testid="signup-waitlist-login">I have an account</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 grain opacity-30" />
        <Link to="/" className="absolute top-8 left-10 flex items-center gap-2 z-10">
          <div className="w-8 h-8 rounded-sm bg-background text-foreground flex items-center justify-center font-black overflow-hidden">
            {branding.logo_icon_url ? (
              <img src={branding.logo_icon_url} alt={branding.platform_name} className="w-full h-full object-cover" />
            ) : (
              (branding.platform_name || "A").slice(0, 1)
            )}
          </div>
          <span className="font-bold tracking-tight">{branding.platform_name}</span>
        </Link>
        <div className="absolute bottom-12 left-10 right-10">
          <div className="label-eyebrow opacity-70 mb-4">— Start free</div>
          <h2 className="heading-display text-5xl xl:text-6xl">One workspace. <br /> Every workflow. <br /> Built for MSMEs.</h2>
          <p className="mt-6 max-w-md opacity-90">Run projects, people, customers and processes from one place.</p>
        </div>
      </div>
      <form onSubmit={submit} className="flex flex-col justify-center px-10 lg:px-20 py-16 max-w-xl w-full mx-auto">
        <Link to="/" className="lg:hidden mb-10 inline-flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black overflow-hidden">
            {branding.logo_icon_url ? <img src={branding.logo_icon_url} alt="" className="w-full h-full object-cover" /> : (branding.platform_name || "A").slice(0, 1)}
          </div>
          <span className="font-bold">{branding.platform_name}</span>
        </Link>
        <div className="label-eyebrow mb-2">Create an account</div>
        <h1 className="heading-display text-4xl mb-10">Get started in 60 seconds.</h1>
        {refCode && (
          <div className="border border-primary/40 bg-primary/5 p-3 text-xs mb-6 flex items-start gap-2" data-testid="signup-referral-notice">
            <span className="chip chip-approved">REFERRED</span>
            <span className="text-muted-foreground">A friend invited you with code <span className="font-mono text-foreground font-bold">{refCode}</span>. Both of you earn 1 month free when you upgrade.</span>
          </div>
        )}
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Full name</Label>
            <Input required value={form.name} onChange={set("name")} className="h-11" data-testid="signup-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Work email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} className="h-11" data-testid="signup-email-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Password</Label>
            <Input type="password" required minLength={8} value={form.password} onChange={set("password")} className="h-11" data-testid="signup-password-input" />
            <p className="text-xs text-muted-foreground mt-1">At least 8 characters.</p>
          </div>
          <Button type="submit" className="w-full h-11 rounded-none font-bold uppercase tracking-widest text-xs" disabled={busy} data-testid="signup-submit-btn">
            {busy ? "Creating account…" : "Create account"}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="underline text-foreground" data-testid="signup-login-link">Log in</Link>
        </p>
        <p className="text-xs text-muted-foreground mt-4">
          By signing up you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
